/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import * as queryString from "query-string";
import { When } from "react-if";
import { generatePath, useParams } from "react-router-dom";
import { Spacer, theme } from "@nordcloud/gnui";
import { BrickLoader, UniversalWrap } from "~/components";
import {
  getColorsPalette,
  usePeriod,
  getTableColumns,
  CostVsBudgetChart,
  BudgetSetupNotification,
  CostTableData,
  getEndDate,
} from "~/components/Charts/CostAnalysis";
import {
  ChartType,
  Granularity,
  Period,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { RouteParams } from "../../types";
import {
  useOrgUnitAccumulatedCostChart,
  useOrgUnitCostVersusBudgetChart,
} from "../hooks";

type Props = {
  orgUnitId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
  forecast: number;
};

export function OrgUnitCostVersusBudgetView({
  orgUnitId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
  forecast,
}: Props) {
  const { currency } = useCurrency();
  const { currentYear } = usePeriod();
  const { structureId = "" } = useParams<RouteParams>();

  const {
    orgUnitAccumulatedCostTotal,
    orgUnitAccumulatedCostTimePoints,
    orgUnitAccumulatedCostBudgetData,
    loading: isOrgUnitCostLoading,
    error: orgUnitCostError,
  } = useOrgUnitAccumulatedCostChart({
    orgUnitId,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const {
    orgUnitBudgetData,
    orgUnitCostVersusBudgetChartData,
    orgUnitBudgetTable,
    orgUnitBudgetTotal,
    hasBudget,
    loading: isBudgetLoading,
    error: budgetError,
  } = useOrgUnitCostVersusBudgetChart({
    orgUnitId,
    currentYear,
    period,
    startDate,
    endDate,
    orgUnitAccumulatedCostBudgetData,
    orgUnitAccumulatedCostTimePoints,
    orgUnitAccumulatedCostTotal,
  });

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: orgUnitBudgetTable,
        hasFooter,
        period,
        chartType: ChartType.COST_VS_BUDGET,
        colorsPalette: getColorsPalette(ChartType.COST_VS_BUDGET),
      }),
    [currency, startDate, endDate, hasFooter, orgUnitBudgetTable]
  );

  const isLoading = isOrgUnitCostLoading || isBudgetLoading;
  const error = orgUnitCostError || budgetError;

  return (
    <>
      <UniversalWrap
        loaderProps={{ loading: isLoading, Component: BrickLoader }}
        errorProps={{ error }}
      >
        <When condition={!hasBudget}>
          <Spacer height={theme.spacing.spacing02} />
          <BudgetSetupNotification
            link={`${generatePath(ROUTES.businessContexts.details, {
              structureId,
              unitId: orgUnitId,
            })}?${queryString.stringify({ costTab: "budget" })}`}
          />
        </When>
        <ParentSize>
          {(parent) => (
            <CostVsBudgetChart
              width={parent.width}
              height={340}
              loading={isLoading}
              data={orgUnitCostVersusBudgetChartData}
              granularity={granularity}
              startDate={startDate}
              endDate={endDate}
              budgetData={orgUnitBudgetData}
              forecastValue={forecast}
            />
          )}
        </ParentSize>
        <CostTableData
          period={period}
          startDate={startDate}
          endDate={endDate}
          tableData={orgUnitBudgetTable}
          chartType={ChartType.COST_VS_BUDGET}
          total={orgUnitBudgetTotal}
          tableError={error}
          columns={columns}
          isLoading={isLoading}
          showCostTable={showCostTable}
          hasFooter={hasFooter}
        />
      </UniversalWrap>
    </>
  );
}
